import { FC, useState, useEffect } from 'react';
import { Paper, Button, Typography, IconButton, Tooltip } from '@mui/material';
import { Controlled as CodeMirror } from 'react-codemirror2';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/components/state/store';
import { setJsonEditorTheme } from '@/components/state/jsonEditor/jsonEditorSlice';
import ModeNightIcon from '@mui/icons-material/ModeNight';
import LightModeIcon from '@mui/icons-material/LightMode';
import WrapTextIcon from '@mui/icons-material/WrapText';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AutoFixOffIcon from '@mui/icons-material/AutoFixOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/material.css';
import 'codemirror/theme/solarized.css';
import 'codemirror/mode/javascript/javascript';
import 'codemirror/addon/fold/foldcode';
import 'codemirror/addon/fold/foldgutter';
import 'codemirror/addon/fold/brace-fold';
import 'codemirror/addon/fold/foldgutter.css';
import styles from '@/components/general/jsonEditor.module.css';

interface Props {
    value?: object;
    saveAsString?: boolean;
    enableSaveButton?: boolean;
    onSave?: (updatedJson: object | string) => void;
    enableCancelButton?: boolean;
    onCancel?: () => void;
    enableFormatButton?: boolean;
    enableLineWrapping?: boolean;
    enableCopyButton?: boolean;
    label?: string;
    mandatory?: boolean;
    noLabel?: boolean;
    containerClassName?: string;
    readonly?: boolean;
    secondaryButton?: {
        label: string;
        onClick: () => void;
    };
    customError?: string | null;
    noTopBar?: boolean;
    noBottomBar?: boolean;
    autoSave?: boolean;
    onEditorMount?: (editor: any) => void;
}

const JsonEditor: FC<Props> = ({
    enableSaveButton = false,
    enableCancelButton = false,
    enableFormatButton = false,
    enableCopyButton = false,
    containerClassName = "",
    readonly = false,
    secondaryButton,
    noTopBar = false,
    noBottomBar = false,
    onEditorMount,
    ...props
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const sampleJson = props.value || [
        { "sampleKey1": "sampleValue1" },
        { "sampleKey2": { "sampleNestedKey": "sampleNestedValue" } }
    ];

    const [jsonString, setJsonString] = useState<string>(JSON.stringify(sampleJson, null, 2));
    const isDarkTheme = useSelector((state: RootState) => state.jsonEditor.isDarkTheme);
    const [lineWrapping, setLineWrapping] = useState<boolean>(true);
    const [autoFormat, setAutoFormat] = useState<boolean>(true);
    const [hasFocus, setHasFocus] = useState<boolean>(false);
    const [copied, setCopied] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const handleSave = () => {
        try {
            const parsedJson = JSON.parse(jsonString);

            if (props.saveAsString) {
                props.onSave?.(JSON.stringify(parsedJson));
            } else {
                props.onSave?.(parsedJson);
            }
        } catch {
            setError(t("fix_json_before_save"));
        }
    };

    const handleFormatInputField = (input: string) => {
        try {
            return JSON.stringify(JSON.parse(input), null, 2);
        } catch {
            return input;
        }
    }

    const handleCopy = () => {
        navigator.clipboard.writeText(jsonString);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const theme = isDarkTheme ? 'material' : 'solarized';

    const toggleTheme = () => {
        const newTheme = !isDarkTheme;
        dispatch(setJsonEditorTheme(newTheme));
    };

    useEffect(() => {
        if (typeof props.value === 'undefined' || hasFocus) return;

        const stringified = typeof props.value === 'string'
            ? props.value
            : (autoFormat ? JSON.stringify(props.value, null, 2) : JSON.stringify(props.value));

        if (stringified !== jsonString) {
            setJsonString(stringified);
            setError(null);
        }
    }, [props.value, autoFormat, hasFocus]);

    return (
        <Paper
            className={`${styles.container} ${containerClassName}`}
            sx={{
                "& .cm-s-solarized .cm-property": { color: isDarkTheme ? '' : '#0070f3' },
                "& .cm-string": { color: isDarkTheme ? '' : '#228B22' },
                "& .cm-s-material.CodeMirror": { backgroundColor: '#101414' },
                "& .cm-s-material .CodeMirror-gutters": { backgroundColor: '#000' },
                "& .cm-s-solarized, & .cm-s-material": { lineHeight: '1.45' },
            }}
        >
            {!noTopBar && (
                <div className={styles.topBarWrapper}>
                    <Typography variant="h6">
                        {props.noLabel ? '' : props.label || t("json_editor")}
                        {props.mandatory && (
                            <span className={styles.labelRequired}>{"\u{002A}"}</span>
                        )}
                    </Typography>
                    <div className={styles.actionButtonsWrapper}>
                        <div>
                            {secondaryButton && !readonly && (
                                <Button color="primary" onClick={secondaryButton?.onClick}>
                                    {secondaryButton?.label}
                                </Button>
                            )}
                        </div>
                        <div>
                            {!readonly && (
                                <>
                                    {autoFormat ? (
                                        <Tooltip arrow placement='bottom' title={t("disable_auto_format")}>
                                            <IconButton
                                                size='small'
                                                onClick={() => setAutoFormat(false)}
                                            >
                                                <AutoFixOffIcon />
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip arrow placement='bottom' title={t("enable_auto_format")}>
                                            <IconButton
                                                size='small'
                                                onClick={() => {
                                                    setAutoFormat(true);
                                                    setJsonString(handleFormatInputField(jsonString));
                                                }}
                                            >
                                                <AutoFixHighIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </>
                            )}
                            {enableCopyButton && (
                                <Tooltip arrow placement='bottom' title={copied ? t("copied") : t("copy_json")}>
                                    <IconButton size='small' onClick={handleCopy}>
                                        <ContentCopyIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {lineWrapping ? (
                                <Tooltip arrow placement='bottom' title={t("disable_line_wrapping")}>
                                    <IconButton
                                        size='small'
                                        onClick={() => setLineWrapping(false)}
                                    >
                                        <FormatAlignLeftIcon />
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                <Tooltip arrow placement='bottom' title={t("enable_line_wrapping")}>
                                    <IconButton
                                        size='small'
                                        onClick={() => setLineWrapping(true)}
                                    >
                                        <WrapTextIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {isDarkTheme ?
                                <Tooltip arrow placement='bottom' title={t("switch_to_light_mode")}>
                                    <IconButton
                                        size='small'
                                        onClick={toggleTheme}
                                    >
                                        <LightModeIcon />
                                    </IconButton>
                                </Tooltip>
                                :
                                <Tooltip arrow placement='bottom' title={t("switch_to_dark_mode")}>
                                    <IconButton
                                        size='small'
                                        onClick={toggleTheme}
                                    >
                                        <ModeNightIcon />
                                    </IconButton>
                                </Tooltip>
                            }
                        </div>
                    </div>
                </div>
            )}
            <div>
                <CodeMirror
                    value={jsonString}
                    options={{
                        mode: { name: 'javascript', json: true },
                        theme: theme,
                        lineNumbers: true,
                        tabSize: 2,
                        foldGutter: true,
                        gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
                        lineWrapping: lineWrapping,
                        readOnly: readonly ? 'nocursor' : false,
                    }}
                    /* eslint-disable */
                    editorDidMount={(editor, value, cb) => {
                        onEditorMount?.(editor);
                        cb?.();
                    }}
                    onFocus={() => setHasFocus(true)}
                    onBeforeChange={(editor, data, value) => {
                        setJsonString(value);
                    }}
                    onBlur={(editor, data) => {
                        setHasFocus(false);
                        let currentValue = editor.getValue();
                        if (autoFormat) {
                            currentValue = handleFormatInputField(currentValue);
                            setJsonString(currentValue);
                        }
                        try {
                            const parsedJson = JSON.parse(currentValue);
                            setError(null);
                            props.onSave && props.onSave(props.saveAsString ? JSON.stringify(parsedJson) : parsedJson);
                        } catch (err) {
                            setError(t("invalid_json"));
                        }
                    }}
                    onChange={(editor, data, value) => {
                        setJsonString(value);
                        try {
                            JSON.parse(value);
                            setError(null);
                        } catch (err) {
                            setError(t("invalid_json"));
                        }
                    }}
                /* eslint-enable */
                />
            </div>
            {!noBottomBar && (
                <>
                    {error && (
                        <Typography color="error" variant="body2" className={styles.errorText}>
                            {error}
                        </Typography>
                    )}
                    {props.customError && (
                        <Typography color="error" variant="body2" className={styles.errorText}>
                            {props.customError}
                        </Typography>
                    )}
                    {!readonly && (
                        <div className={styles.actionButtonsWrapper}>
                            <div className={styles.secondaryActionsButtonsWrapper}>
                                {enableFormatButton && !autoFormat && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size='small'
                                        onClick={() => setJsonString(handleFormatInputField(jsonString))}
                                    >
                                        {t("format")}
                                    </Button>
                                )}
                                {enableCancelButton && (
                                    <Button
                                        size='small'
                                        onClick={props.onCancel}
                                    >
                                        {t("cancel")}
                                    </Button>
                                )}
                            </div>
                            {enableSaveButton && (
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    size='small'
                                    onClick={handleSave}
                                    disabled={!!error}
                                >
                                    {t("save")}
                                </Button>
                            )}
                        </div>
                    )}
                </>
            )}
        </Paper>
    );
};

export default JsonEditor;
